import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { clientApi } from '@/api/client.api';
import { leasingScoringApi } from '@/api/leasing-scoring.api';
import type { TClientDetailedInfo } from '@/pages/ClientInfo/types/client.types';
import { keysToCamelCase } from '@/shared/services/tools';
import type { TLeasingFile } from '@/shared/types/files.types';
import type {
  IScoringInfoApi,
  TScoringInfo
} from '@/shared/types/scoring-info.types';

import type { TRootState } from '.';

export const getClientById = createAsyncThunk(
  'client/getClientById',
  async (id: string, { getState }) => {
    const { userInfo } = getState() as TRootState;
    const response = await clientApi.getClientById(id);

    const newestRequest = response.Requests?.sort(
      (a, b) =>
        new Date(b.create_date).getTime() - new Date(a.create_date).getTime()
    )[0];
    const decision = newestRequest?.Decisions?.[0];
    let scoringInfo: IScoringInfoApi | null = null;
    if (
      decision?.uuid &&
      (userInfo?.userAccesses.global.isUnderwriter ||
        userInfo?.userAccesses.global.isAdmin)
    ) {
      try {
        scoringInfo = await leasingScoringApi.getScoringInfo(decision.uuid);
        if (scoringInfo.risk_zone) {
          scoringInfo.risk_zone = scoringInfo.risk_zone?.toLowerCase() as
            | 'red'
            | 'yellow'
            | 'green';
        }
      } catch (err) {
        scoringInfo = null;
        console.error(err);
      }
    }

    return { client: response, scoringInfo };
  }
);

interface IClientState {
  client: TClientDetailedInfo | null;
  scoringInfo: TScoringInfo | null;
}

const initialState: IClientState = {
  client: null,
  scoringInfo: null
};

const clientInfoSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    resetClientInfo: (state) => {
      state.client = null;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getClientById.fulfilled, (state, action) => {
      const { Requests, ...rest } = action.payload.client;
      state.client = {
        ...keysToCamelCase(rest),
        requests:
          Requests?.map((request) => {
            const { Files, ...requestRest } = request;
            return {
              ...keysToCamelCase(requestRest),
              files: Files?.reduce(
                (acc, file) => {
                  acc[file.file_type.code] = keysToCamelCase(file);
                  return acc;
                },
                {} as Record<string, TLeasingFile>
              )
            };
          }) || []
      };
      state.scoringInfo = keysToCamelCase(action.payload.scoringInfo);
    });
  }
});

export const { resetClientInfo } = clientInfoSlice.actions;

export default clientInfoSlice;
